import Box from '@mui/material/Box';
import { colors, Tag } from '@sweep-io/sweep-design';
import {
  configurationTypeToIcon,
  DependenciesContentViewType,
  SelectedDependencyWithClearHistoryProps,
} from '../dependencies/types';
import { CenteredRowWithGap } from './atoms/CenteredRowWithGap';
import Stack from '@mui/material/Stack';
import { renderToStaticMarkup } from 'react-dom/server';
import { highlightMatch } from '../../../lib/highlightMatch';
import { ClampedText } from '../../common/ClampedText';
import { TruncatedTextTooltip } from '../../common/TruncatedTextTooltip';
import { InactiveTag } from '../activity-state/InactiveTag';
import { translateTypeToHumanName, getObjectNameColor } from '../helpers';
import { WrapperWithDotDivider } from './molecules/WrapperWithDotDivider';
import { Connect } from '@sweep-io/sweep-design/dist/icons';
import { ConfigurationItem, isFieldConfigurationItem } from '../../parser/ParserTypes';
import { humanizeDate } from '../../helpers/humanizeDate';

interface UniversalSearchListItemProps {
  searchTxt: string;
  item: ConfigurationItem;
  onListItemClick: (item: SelectedDependencyWithClearHistoryProps) => void;
  activeItemId?: string;
  hoveredItemId?: string;
  renderRightElement?: (item: ConfigurationItem, isHovered: boolean) => React.ReactNode;
  getItemStyling?: (item: ConfigurationItem) => object;
  forceRenderIcons: boolean;
  setHoveredItemId?: (id: string | null) => void;
  isExpandedMode: boolean;
}

export const UniversalSearchListItem = ({
  searchTxt,
  item,
  onListItemClick,
  activeItemId,
  hoveredItemId,
  renderRightElement,
  forceRenderIcons,
  setHoveredItemId,
  getItemStyling,
  isExpandedMode,
}: UniversalSearchListItemProps) => {
  const id = !!item.id ? item.id : undefined;
  const _id = id ?? item.name ?? item.metadataId;

  const isActive = !!item.isActive;
  const objectApiName = item.objectApiNames?.[0] ?? '';
  const isHovered = hoveredItemId === _id;
  const rightElement = renderRightElement?.(item, isHovered);
  const customStyling = getItemStyling?.(item) || {};
  const fieldType = isFieldConfigurationItem(item) ? item.fieldType : undefined;

  return (
    <Box
      key={_id}
      p={1.5}
      pl={2}
      gap={'6px'}
      display="flex"
      flexDirection="column"
      sx={{
        width: '100%',
        background: activeItemId === _id ? colors.grey[100] : 'initial',
        '&:hover': { background: colors.grey[100], cursor: 'pointer' },

        '& .MuiChip-label': {
          color: isActive ? 'inherit' : colors.grey[500],
        },
        ...customStyling,
      }}
      onMouseEnter={() => setHoveredItemId?.(_id)}
      onMouseLeave={() => setHoveredItemId?.(null)}
      onClick={() =>
        onListItemClick({
          id: item.id,
          name: item.name,
          dependencyType: isFieldConfigurationItem(item) ? item.fieldType : undefined,
          parentType: item.parentType,
          objectName: item.objectApiNames?.[0] ?? '',
          clearHistory: true,
          contentType:
            item.match !== undefined
              ? DependenciesContentViewType.sourceCode
              : DependenciesContentViewType.default,
          metadataId: item.metadataId,
          label: item.label,
        })
      }
      data-testid={`universal-search-list-item-${_id}`}
    >
      <CenteredRowWithGap
        gap={0.5}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: rightElement ? 'space-between' : 'flex-start',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {(isExpandedMode || forceRenderIcons) && (
            <Stack alignItems="center" pr={1} sx={{ flexShrink: 0 }}>
              {configurationTypeToIcon[item.parentType] ?? <Connect size="medium" />}
            </Stack>
          )}

          <Box
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              flexShrink: 1,
              '& .MuiTypography-root': {
                display: 'block',
                overflow: 'hidden',
                textOverflow: isExpandedMode ? 'clip' : 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          >
            <TruncatedTextTooltip
              color={isActive ? 'inherit' : colors.grey[500]}
              variant="body"
              turnOffElipsis={isExpandedMode}
            >
              {!!item.match ? item.label : highlightMatch(item.label, searchTxt)}
            </TruncatedTextTooltip>
          </Box>

          <CenteredRowWithGap sx={{ flexShrink: 0 }}>
            <Tag
              size="tiny"
              color={colors.grey[120]}
              label={translateTypeToHumanName(item.parentType)}
            />
          </CenteredRowWithGap>
          <Box ml={-0.5} display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
            <InactiveTag isActive={isActive} />
          </Box>
        </Box>

        {rightElement && (
          <Box
            display="flex"
            alignItems="center"
            sx={{
              flexShrink: 0,
              ml: 1,
            }}
          >
            {rightElement}
          </Box>
        )}
      </CenteredRowWithGap>

      <CenteredRowWithGap ml={isExpandedMode ? 3.5 : 0}>
        <TruncatedTextTooltip
          variant="caption"
          color={isActive ? colors.grey[700] : colors.grey[500]}
          turnOffElipsis={isExpandedMode}
        >
          API Name: {!!item.match ? item.name : highlightMatch(item.name)}
        </TruncatedTextTooltip>

        {fieldType && <WrapperWithDotDivider isActive={isActive} txt={fieldType} />}
        {item.objectApiNames?.[0] && isActive && (
          <WrapperWithDotDivider
            isActive={isActive}
            txt={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Tag
                  size="tiny"
                  label={objectApiName}
                  color={getObjectNameColor(objectApiName) ?? colors.grey[100]}
                />
              </Box>
            }
          />
        )}
        {item.objectApiNames?.[0] && !isActive && (
          <WrapperWithDotDivider isActive={isActive} txt={item.objectApiNames?.[0] ?? ''} />
        )}

        {isExpandedMode && (
          <>
            {item.updatedAt && (
              <WrapperWithDotDivider
                isActive={isActive}
                txt={`Last modified ${humanizeDate({ dateOrTimestamp: item.updatedAt })}`}
              />
            )}

            {item.createdAt && (
              <WrapperWithDotDivider
                isActive={isActive}
                txt={`Created by ${item.createdBy} on ${humanizeDate({ dateOrTimestamp: item.createdAt })}`}
              />
            )}
          </>
        )}
      </CenteredRowWithGap>

      {!!item.match && (
        <CenteredRowWithGap ml={isExpandedMode ? 3.5 : 0}>
          <ClampedText
            text={highlightMatch(unescape(renderToStaticMarkup(item.match?.trim())), searchTxt)}
            typographyVariant="caption"
            textColor={isActive ? colors.grey[700] : colors.grey[500]}
          />
        </CenteredRowWithGap>
      )}
    </Box>
  );
};
